import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Avatar } from '../components/Avatar';
import { Button } from '../components/ui';
import { EmptyState } from '../components/EmptyState';
import { socialService, Friendship } from '../services/socialService';
import { useAuth } from '../hooks/useAuth';
import { debugLogger } from '../utils/debugLogger';

export const FriendRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser, logout } = useAuth();
  const [friendRequests, setFriendRequests] = useState<Friendship[]>([]);
  const [sentRequests, setSentRequests] = useState<Friendship[]>([]);
  const [activeTab, setActiveTab] = useState<'received' | 'sent'>('received');
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState<number | null>(null);

  useEffect(() => {
    if (!currentUser) {
      navigate('/auth');
      return;
    }
    loadFriendRequests();
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'sent') {
      setActiveTab('sent');
    } else if (tabParam === 'received') {
      setActiveTab('received');
    }
  }, [currentUser, navigate, location.search]);

  const loadFriendRequests = async () => {
    try {
      setIsLoading(true);
      const [receivedResponse, sentResponse] = await Promise.all([
        socialService.getFriendRequests(0, 50),
        socialService.getSentFriendRequests(0, 50)
      ]);
      
      let normalizedReceived: any[] = [];
      const rawReceived: any = receivedResponse as any;
      if (Array.isArray(rawReceived?.content)) {
        normalizedReceived = rawReceived.content;
      } else if (Array.isArray(rawReceived?.data?.friendships)) {
        normalizedReceived = rawReceived.data.friendships.map((f: any) => ({
          id: f.friendshipId || 0,
          status: (f.status || 'PENDING').toUpperCase(),
          createdAt: f.createdAt,
          requester: {
            id: f.userId,
            username: f.username,
            displayName: f.displayName,
            avatarUrl: f.avatarUrl,
            bio: '',
            isVerified: false,
            isPrivate: false,
          },
        }));
      }
      setFriendRequests(normalizedReceived);

      let normalizedSent: any[] = [];
      const rawAny: any = sentResponse as any;
      if (Array.isArray(rawAny?.content)) {
        normalizedSent = rawAny.content;
      } else if (Array.isArray(rawAny?.data?.friendships)) {
        normalizedSent = rawAny.data.friendships.map((f: any) => ({
          id: f.friendshipId || 0,
          status: (f.status || 'PENDING').toUpperCase(),
          createdAt: f.createdAt,
          addressee: {
            id: f.userId,
            username: f.username,
            displayName: f.displayName,
            avatarUrl: f.avatarUrl,
            bio: '',
            isVerified: false,
            isPrivate: false,
          },
        }));
      }
      setSentRequests(normalizedSent);
      
      debugLogger.log('FriendRequestsPage', 'Loaded friend requests', {
        received: Array.isArray((receivedResponse as any)?.content) ? (receivedResponse as any).content.length : ((receivedResponse as any)?.data?.friendships || []).length,
        sent: Array.isArray((sentResponse as any)?.content) ? (sentResponse as any).content.length : ((sentResponse as any)?.data?.friendships || []).length
      });
    } catch (error) {
      debugLogger.log('FriendRequestsPage', 'Failed to load friend requests', error);
      console.error('Failed to load friend requests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptRequest = async (friendshipIdOrUserId: number, userIdFallback?: number) => {
    try {
      setIsProcessing(friendshipIdOrUserId);
      try {
        const otherUserId = userIdFallback ?? friendshipIdOrUserId;
        await socialService.acceptFriendRequest({ otherUserId });
      } catch (e) {
        if (userIdFallback) {
          await socialService.acceptFriendRequestByUser(userIdFallback);
        } else {
          throw e;
        }
      }
      
      // Remove from received requests
      setFriendRequests(prev => prev.filter((req: any) => (req.friendshipId || req.id) !== friendshipIdOrUserId));
      
      debugLogger.log('FriendRequestsPage', 'Friend request accepted', { friendshipId: friendshipIdOrUserId });
    } catch (error) {
      debugLogger.log('FriendRequestsPage', 'Failed to accept friend request', error);
      console.error('Failed to accept friend request:', error);
    } finally {
      setIsProcessing(null);
    }
  };

  const handleRejectRequest = async (friendshipIdOrUserId: number, userIdFallback?: number) => {
    try {
      setIsProcessing(friendshipIdOrUserId);
      try {
        const otherUserId = userIdFallback ?? friendshipIdOrUserId;
        await socialService.rejectFriendRequest({ otherUserId });
      } catch (e) {
        if (userIdFallback) {
          await socialService.rejectFriendRequestByUser(userIdFallback);
        } else {
          throw e;
        }
      }
      
      setFriendRequests(prev => prev.filter((req: any) => (req.friendshipId || req.id) !== friendshipIdOrUserId));
      
      debugLogger.log('FriendRequestsPage', 'Friend request rejected', { friendshipId: friendshipIdOrUserId });
    } catch (error) {
      debugLogger.log('FriendRequestsPage', 'Failed to reject friend request', error);
      console.error('Failed to reject friend request:', error);
    } finally {
      setIsProcessing(null);
    }
  };

  const handleCancelRequest = async (friendshipId: number, otherUserId?: number) => {
    try {
      setIsProcessing(friendshipId);
      if (otherUserId) {
        await socialService.rejectFriendRequest({ otherUserId });
      } else {
        await socialService.rejectFriendRequestByUser(friendshipId);
      }
      
      // Remove from sent requests
      setSentRequests(prev => prev.filter(req => req.id !== friendshipId));
      
      debugLogger.log('FriendRequestsPage', 'Friend request cancelled', { friendshipId });
    } catch (error) {
      debugLogger.log('FriendRequestsPage', 'Failed to cancel friend request', error);
      console.error('Failed to cancel friend request:', error);
    } finally {
      setIsProcessing(null);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/auth');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleUserClick = (userId: number) => {
    navigate(`/profile/${userId}`);
  };

  if (!currentUser) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/20 to-gray-50">
      <Header showSearch={false} showUserMenu={true} onLogout={handleLogout} />
      
      <main className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Hero Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-3 hover:bg-white/80 rounded-xl transition-all shadow-sm hover:shadow-md border border-gray-200/50 backdrop-blur-sm group"
              >
                <svg className="w-5 h-5 text-gray-600 group-hover:text-primary-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 via-blue-900 to-gray-900 bg-clip-text text-transparent">
                  Lời mời kết bạn
                </h1>
                <p className="text-gray-600 mt-1">Quản lý lời mời kết bạn của bạn</p>
              </div>
            </div>
          </div>

          {/* Navigation Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Link to="/friends" className="group">
              <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-200/50 p-4 hover:shadow-lg hover:border-primary-300 transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg shadow-md group-hover:shadow-primary-200 transition-shadow">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">Bạn bè</h3>
                  </div>
                  <svg className="w-4 h-4 text-gray-400 group-hover:text-primary-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>

            <Link to="/blocked-users" className="group">
              <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-200/50 p-4 hover:shadow-lg hover:border-red-300 transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-md group-hover:shadow-red-200 transition-shadow">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 group-hover:text-red-600 transition-colors">Đã chặn</h3>
                  </div>
                  <svg className="w-4 h-4 text-gray-400 group-hover:text-red-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>

            <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-lg p-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-primary-100 mb-0.5">Tổng lời mời</p>
                  <p className="text-2xl font-bold">{friendRequests.length + sentRequests.length}</p>
                </div>
                <div className="p-2.5 bg-white/20 rounded-lg backdrop-blur-sm">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modern Tabs */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-gray-200/50 p-2 mb-6">
          <div className="flex space-x-2">
            <button
              onClick={() => setActiveTab('received')}
              className={`flex-1 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${
                activeTab === 'received'
                  ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/30'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                <span>Đã nhận</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  activeTab === 'received' 
                    ? 'bg-white/20 text-white' 
                    : 'bg-primary-100 text-primary-700'
                }`}>
                  {friendRequests.length}
                </span>
              </span>
            </button>
            <button
              onClick={() => setActiveTab('sent')}
              className={`flex-1 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${
                activeTab === 'sent'
                  ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/30'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                <span>Đã gửi</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  activeTab === 'sent' 
                    ? 'bg-white/20 text-white' 
                    : 'bg-primary-100 text-primary-700'
                }`}>
                  {sentRequests.length}
                </span>
              </span>
            </button>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <EmptyState type="loading" title="Đang tải lời mời kết bạn..." />
        ) : (
          <>
            {/* Received Requests */}
            {activeTab === 'received' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(friendRequests && friendRequests.length > 0) ? (
                  friendRequests.map((request: any) => (
                    <div key={request.id} className="group bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-gray-200/50 hover:shadow-xl hover:border-primary-200 transition-all duration-300 overflow-hidden transform hover:-translate-y-1">
                      <div className="p-6">
                        <div className="flex items-start space-x-4 mb-4">
                          <div 
                            className="cursor-pointer relative"
                            onClick={() => handleUserClick(request.requester?.id || request.requesterId)}
                          >
                            <Avatar
                              user={request.requester}
                              size="lg"
                              className="w-20 h-20 ring-4 ring-white shadow-lg"
                            />
                            <div className="absolute -bottom-1 -right-1 p-2 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full shadow-md">
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            </div>
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div 
                              className="cursor-pointer"
                              onClick={() => handleUserClick(request.requester?.id || request.requesterId)}
                            >
                              <h3 className="text-lg font-bold text-gray-900 hover:text-primary-600 transition-colors truncate">
                                {request.requester?.displayName}
                              </h3>
                              <p className="text-sm text-gray-500 mb-2">@{request.requester?.username}</p>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                                <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                </svg>
                                Lời mời mới
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {request.requester?.bio && (
                          <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                            {request.requester?.bio}
                          </p>
                        )}
                        
                        <div className="flex items-center justify-between mb-4">
                          <p className="text-xs text-gray-400 flex items-center space-x-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{new Date(request.createdAt).toLocaleDateString('vi-VN')}</span>
                          </p>
                        </div>
                        
                        <div className="flex space-x-3 pt-4 border-t border-gray-100">
                          <button
                            onClick={() => handleAcceptRequest((request as any).friendshipId || request.id, request.requester?.id || (request as any).requesterId)}
                            disabled={isProcessing === ((request as any).friendshipId || request.id)}
                            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 font-semibold"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>{isProcessing === ((request as any).friendshipId || request.id) ? '...' : 'Chấp nhận'}</span>
                          </button>
                          <button
                            onClick={() => handleRejectRequest((request as any).friendshipId || request.id, request.requester?.id || (request as any).requesterId)}
                            disabled={isProcessing === ((request as any).friendshipId || request.id)}
                            className="px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl transition-all duration-300 border border-red-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 font-semibold"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span>Từ chối</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-2 bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-gray-200/50 p-12 text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full mb-6">
                      <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Không có lời mời kết bạn</h3>
                    <p className="text-gray-500">Bạn chưa nhận được lời mời kết bạn nào.</p>
                  </div>
                )}
              </div>
            )}

            {/* Sent Requests */}
            {activeTab === 'sent' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(sentRequests && sentRequests.length > 0) ? (
                  sentRequests.map((request: any) => (
                    <div key={request.id} className="group bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-gray-200/50 hover:shadow-xl hover:border-amber-200 transition-all duration-300 overflow-hidden transform hover:-translate-y-1">
                      <div className="p-6">
                        <div className="flex items-start space-x-4 mb-4">
                          <div 
                            className="cursor-pointer relative"
                            onClick={() => handleUserClick(request.addressee?.id)}
                          >
                            <Avatar
                              user={request.addressee}
                              size="lg"
                              className="w-20 h-20 ring-4 ring-white shadow-lg"
                            />
                            <div className="absolute -bottom-1 -right-1 p-2 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full shadow-md">
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                              </svg>
                            </div>
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div 
                              className="cursor-pointer"
                              onClick={() => handleUserClick(request.addressee?.id)}
                            >
                              <h3 className="text-lg font-bold text-gray-900 hover:text-primary-600 transition-colors truncate">
                                {request.addressee?.displayName}
                              </h3>
                              <p className="text-sm text-gray-500 mb-2">@{request.addressee?.username}</p>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${
                                request.status === 'PENDING' 
                                  ? 'bg-amber-100 text-amber-700 border-amber-200'
                                  : request.status === 'ACCEPTED'
                                  ? 'bg-green-100 text-green-700 border-green-200'
                                  : 'bg-red-100 text-red-700 border-red-200'
                              }`}>
                                <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {request.status === 'PENDING' ? 'Đang chờ' :
                                 request.status === 'ACCEPTED' ? 'Đã chấp nhận' :
                                 'Đã từ chối'}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {request.addressee?.bio && (
                          <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                            {request.addressee?.bio}
                          </p>
                        )}
                        
                        <div className="flex items-center justify-between mb-4">
                          <p className="text-xs text-gray-400 flex items-center space-x-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>Đã gửi {new Date(request.createdAt).toLocaleDateString('vi-VN')}</span>
                          </p>
                        </div>
                        
                        <div className="pt-4 border-t border-gray-100">
                          {request.status === 'PENDING' && request.id !== 0 ? (
                            <button
                              onClick={() => handleCancelRequest(request.id, request.addressee?.id)}
                              disabled={isProcessing === request.id}
                              className="w-full px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl transition-all duration-300 border border-red-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 font-semibold"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              <span>{isProcessing === request.id ? 'Đang hủy...' : 'Hủy lời mời'}</span>
                            </button>
                          ) : (
                            <div className="text-center py-2">
                              <span className={`inline-flex items-center px-4 py-2 rounded-xl text-sm font-semibold ${
                                request.status === 'ACCEPTED'
                                  ? 'bg-green-100 text-green-700 border-2 border-green-200'
                                  : 'bg-gray-100 text-gray-600 border-2 border-gray-200'
                              }`}>
                                {request.status === 'ACCEPTED' ? (
                                  <>
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Đã chấp nhận
                                  </>
                                ) : (
                                  <>
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    Đã từ chối
                                  </>
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-2 bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-gray-200/50 p-12 text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full mb-6">
                      <svg className="w-10 h-10 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Chưa gửi lời mời nào</h3>
                    <p className="text-gray-500">Bạn chưa gửi lời mời kết bạn nào.</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default FriendRequestsPage;
